/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import isUndefined from 'lodash.isundefined';
import {getSubGroupsPath} from '@ion/applications/src/utils/helpers';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Preference as GlobalPreference} from '@ion/core/src/types/preferences';

interface UseSubAccountsToggleSetupProps {
  preference?: string;
  defaultValue?: boolean;
}

/**
 * Defines the useSubAccountsToggleSetup component. This component is reusable and set the
 * subAccountChecked state based on `includeSubAccountsToggleSetup` UserPreferences.
 *
 * ```typescript
 *
 * const {subAccountChecked, setSubAccountChecked} = useSubAccountsToggleSetup({checked, onChecked, onChange});
 * 
 * @param  onChecked object will take precedence when passed as an param.
 * @param  onChange object will take lower precedence over onChecked and 
 *         need to pass checked an boolean param to it.
 *
 * ```
 */
const useSubAccountsToggleSetup = ({preference = '', defaultValue = false}: UseSubAccountsToggleSetupProps) => {
  const pageSubGroupView                          = UserPreferences.get(getSubGroupsPath(preference));
  const toggleChecked                             = UserPreferences.get(GlobalPreference.includeSubAccountsToggleSetup);
  const [subAccountChecked, setSubAccountChecked] = useState(
    preference ? (pageSubGroupView ?? toggleChecked) : defaultValue
  );
  useEffect(() => {
    if (preference && isUndefined(pageSubGroupView) && subAccountChecked !== toggleChecked) {
      setSubAccountChecked(toggleChecked);
    }
  }, [toggleChecked]);

  return {
    subAccountChecked,
    setSubAccountChecked
  };
};

export default useSubAccountsToggleSetup;
